import * as THREE from 'three';

/**
 * Room defines the playable arena for combat.
 *
 * A room consists of:
 * - Rectangular bounds (width x height)
 * - Visual floor (dark gray plane)
 * - Visual walls (cuboids around perimeter)
 * - Collision detection to keep entities inside
 *
 * Visual style:
 * - Floor: Medium gray
 * - Walls: Darker gray/brown cuboids
 * - Primitive geometric aesthetic
 */
export class Room {
  readonly width: number;
  readonly height: number;
  readonly mesh: THREE.Group;

  /**
   * Wall height for visual representation.
   */
  private readonly wallHeight: number = 2;

  /**
   * Wall thickness for visual representation.
   */
  private readonly wallThickness: number = 0.5;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.mesh = new THREE.Group();

    this.createFloor();
    this.createWalls();
  }

  /**
   * Create the floor plane for the room.
   */
  private createFloor(): void {
    const geometry = new THREE.PlaneGeometry(this.width, this.height);
    const material = new THREE.MeshStandardMaterial({
      color: 0x4a4a5e, // Medium gray
      roughness: 0.8,
      metalness: 0.2,
    });

    const floor = new THREE.Mesh(geometry, material);
    floor.rotation.x = -Math.PI / 2; // Rotate to be horizontal
    floor.position.y = 0;

    this.mesh.add(floor);
  }

  /**
   * Create walls around the perimeter of the room.
   *
   * Walls are represented as cuboids positioned at the edges of the room.
   */
  private createWalls(): void {
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0x3a3a48, // Darker gray-brown
      roughness: 0.7,
      metalness: 0.3,
    });

    const halfWidth = this.width / 2;
    const halfHeight = this.height / 2;
    const halfWallHeight = this.wallHeight / 2;

    // North wall (along +Z edge)
    const northWall = this.createWall(
      this.width + this.wallThickness * 2,
      this.wallThickness,
      wallMaterial
    );
    northWall.position.set(0, halfWallHeight, halfHeight + this.wallThickness / 2);

    // South wall (along -Z edge)
    const southWall = this.createWall(
      this.width + this.wallThickness * 2,
      this.wallThickness,
      wallMaterial
    );
    southWall.position.set(0, halfWallHeight, -halfHeight - this.wallThickness / 2);

    // East wall (along +X edge)
    const eastWall = this.createWall(
      this.wallThickness,
      this.height,
      wallMaterial
    );
    eastWall.position.set(halfWidth + this.wallThickness / 2, halfWallHeight, 0);

    // West wall (along -X edge)
    const westWall = this.createWall(
      this.wallThickness,
      this.height,
      wallMaterial
    );
    westWall.position.set(-halfWidth - this.wallThickness / 2, halfWallHeight, 0);

    this.mesh.add(northWall, southWall, eastWall, westWall);
  }

  /**
   * Helper to create a single wall mesh.
   *
   * @param width - Wall width (X dimension)
   * @param depth - Wall depth (Z dimension)
   * @param material - Material to use for the wall
   */
  private createWall(
    width: number,
    depth: number,
    material: THREE.Material
  ): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(width, this.wallHeight, depth);
    return new THREE.Mesh(geometry, material);
  }

  /**
   * Check if a position is inside the room bounds.
   *
   * @param x - X coordinate
   * @param y - Y coordinate (game Y, maps to Three.js Z)
   * @returns true if position is within bounds
   */
  containsPoint(x: number, y: number): boolean {
    const halfWidth = this.width / 2;
    const halfHeight = this.height / 2;

    return (
      x >= -halfWidth &&
      x <= halfWidth &&
      y >= -halfHeight &&
      y <= halfHeight
    );
  }

  /**
   * Clamp a position to stay within room bounds.
   *
   * This is used to enforce wall collision - positions outside
   * the room are pushed back inside.
   *
   * @param pos - Position to clamp
   * @param margin - Distance from wall edge to keep (default 0.8 units for entity radius)
   * @returns Clamped position within room bounds
   */
  clampPosition(pos: { x: number; y: number }, margin: number = 0.8): { x: number; y: number } {
    const halfWidth = this.width / 2 - margin;
    const halfHeight = this.height / 2 - margin;

    return {
      x: Math.max(-halfWidth, Math.min(halfWidth, pos.x)),
      y: Math.max(-halfHeight, Math.min(halfHeight, pos.y)),
    };
  }
}
